import { css } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { Key, useMemo, type JSX } from 'react';
import { IFormField } from '../../../view-models/form.view-model';
import { ConfigStateChip } from '../components/ConfigStateChip';
import { DetailsConfigActions } from './DetailsConfigActions';
import { ExitConfigBlocker } from './ExitConfigBlocker';
import { resolvableSelectedFields } from './data-display-utils';
import { FieldOrderConfig } from './FieldOrderConfig';
import { useDisplayConfigState } from './useDisplayConfigState';

const panelContainer = css`
  display: grid;
  grid-template-columns: 1fr minmax(30%, 26rem);
  column-gap: 1rem;
  overflow: hidden;
`;

const detailsPanelContainer = css`
  align-self: start;
`;

export interface IDisplayConfigStateParams {
  selectedFields?: string[];
  setSelectedFields: (fields: string[]) => void;
  handleSaveChanges: () => Promise<void>;
  handleResetConfig: () => Promise<void>;
  fieldsPerPage: number;
}

export interface IDummySectionProps {
  defaultPage: number;
  key: Key;
}
interface IDisplayConfigProps extends IDisplayConfigStateParams {
  title: string;
  allFields: IFormField<unknown>[];
  renderDummySection: (props: IDummySectionProps) => JSX.Element;
  /** See `FieldOrderConfig`. Sections that show a "Show All" link rather than pages pass
   *  `visibility`, so the field groups are labelled by what the user actually sees. */
  groupLabelMode?: 'pages' | 'visibility';
}

export function DisplayConfig({
  title,
  handleSaveChanges,
  handleResetConfig,
  selectedFields,
  setSelectedFields,
  fieldsPerPage,
  allFields,
  renderDummySection,
  groupLabelMode,
}: IDisplayConfigProps) {
  // What the config edits and saves: a key whose field is gone (a deleted custom field, say) is
  // ignored here, so the next save writes it out. `FieldOrderConfig` pages over the same list,
  // which keeps the page a field is on and the `index` it reports back in step.
  const resolvedSelectedFields = useMemo(
    () => (selectedFields ? resolvableSelectedFields(selectedFields, allFields) : selectedFields),
    [allFields, selectedFields]
  );

  const { onSave, onReset, editStatus, activePage, onRemoveField, onReorder, onAddField } =
    useDisplayConfigState({
      selectedFields: resolvedSelectedFields,
      setSelectedFields,
      handleSaveChanges,
      handleResetConfig,
      fieldsPerPage,
    });

  const key = useMemo(() => selectedFields?.join(',') ?? '', [selectedFields]);

  const dummySection = useMemo(
    () => renderDummySection({ defaultPage: activePage, key: key }),
    [activePage, key, renderDummySection]
  );

  return (
    <Stack height='100%'>
      <ExitConfigBlocker onSave={onSave} onLeave={onReset} editStatus={editStatus} />
      <Stack direction='row' alignItems={'start'} columnGap='0.5rem'>
        <Typography variant='h4' mb='1rem'>
          {title}
        </Typography>
        <ConfigStateChip configStatus={editStatus} />
      </Stack>
      <Stack direction={'row'} gap={'1rem'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography variant='body2' color='text.secondary'>
          Customize the field order for this section. To apply all changes, press the button “Save Changes”
        </Typography>
        <DetailsConfigActions
          save={{ handler: onSave, disabled: editStatus !== 'modified' }}
          reset={{ handler: onReset }}
        />
      </Stack>
      <Box margin={'1rem 0'} css={panelContainer}>
        <motion.div key={key} animate={{ opacity: [0.5, 1] }} transition={{ duration: 1 }}>
          <Box css={detailsPanelContainer}>{dummySection}</Box>
        </motion.div>
        <FieldOrderConfig
          selectedFields={resolvedSelectedFields as string[]}
          onRemoveField={onRemoveField}
          onReorder={onReorder}
          onAddField={onAddField}
          allFields={allFields}
          groupLabelMode={groupLabelMode}
        />
      </Box>
    </Stack>
  );
}
